import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

import { getDefaultResolvedConfig, loadPlatformUsageConfig } from './configLoader';
import { ResolvedPlatformUsageConfig } from './types';

interface PlatformUsageConfigContextType {
  config: ResolvedPlatformUsageConfig;
  isLoading: boolean;
  error: Error | null;
}

const PlatformUsageConfigContext = createContext<PlatformUsageConfigContextType | undefined>(undefined);

interface PlatformUsageConfigProviderProps {
  children: ReactNode;
}

/**
 * Provider component that loads configuration from YAML and makes it available to children.
 * Starts with default config and updates once YAML is loaded.
 */
export function PlatformUsageConfigProvider({ children }: PlatformUsageConfigProviderProps) {
  const [config, setConfig] = useState<ResolvedPlatformUsageConfig>(getDefaultResolvedConfig);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    loadPlatformUsageConfig()
      .then((loadedConfig) => {
        if (mounted) {
          setConfig(loadedConfig);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (mounted) {
          console.error('[PlatformUsageConfig] Failed to load configuration:', err);
          setError(err instanceof Error ? err : new Error(String(err)));
          setIsLoading(false);
          // Keep using default config on error
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <PlatformUsageConfigContext.Provider value={{ config, isLoading, error }}>
      {children}
    </PlatformUsageConfigContext.Provider>
  );
}

/**
 * Hook to access the platform usage configuration.
 * Returns the resolved config with typed enums and computed dates.
 *
 * @throws Error if used outside of PlatformUsageConfigProvider
 */
export function usePlatformUsageConfig(): ResolvedPlatformUsageConfig {
  const context = useContext(PlatformUsageConfigContext);
  if (context === undefined) {
    throw new Error('usePlatformUsageConfig must be used within a PlatformUsageConfigProvider');
  }
  return context.config;
}

/**
 * Hook to access full config context including loading state and errors.
 * Useful for showing loading indicators or error messages.
 *
 * @throws Error if used outside of PlatformUsageConfigProvider
 */
export function usePlatformUsageConfigContext(): PlatformUsageConfigContextType {
  const context = useContext(PlatformUsageConfigContext);
  if (context === undefined) {
    throw new Error('usePlatformUsageConfigContext must be used within a PlatformUsageConfigProvider');
  }
  return context;
}
